/**
 * Hands a started player's sign-up to the game's own Worker (POST
 * /api/signup), which passes it to the HubSpot form (worker/signup.js).
 * Fire and forget: it returns nothing to wait on and never throws, so the
 * game start is never held up and the player never sees a failure.
 *
 * At most one sign-up per email per tab: PLAY AGAIN reloads the page and
 * starts a new run, and each submission would otherwise add to the contact's
 * timeline and enrol them again in any form-triggered workflow. The one
 * exception is ticking the marketing box on a later game, which is sent once
 * more so the consent is recorded. The Worker's database look-up applies the
 * same rule across tabs and devices (signup_for_run).
 */

export const SIGNUP_PATH = '/api/signup';
/** sessionStorage: {email, consent} of the sign-up this tab already sent. */
export const SENT_KEY = 'receipthunt.signupSent';

/**
 * The session's storage, or null where there is none (node, blocked storage).
 * @returns {Storage|null}
 */
function sessionStore() {
  try {
    return globalThis.sessionStorage || null;
  } catch (e) {
    return null;
  }
}

/**
 * Whether this tab already sent a sign-up that covers this one: the same
 * email, and either the box was ticked then or it isn't now.
 * @param {string} email
 * @param {boolean} consent
 * @param {Storage|null} storage
 * @returns {boolean}
 */
export function alreadySent(email, consent, storage) {
  try {
    const sent = JSON.parse(storage.getItem(SENT_KEY) || 'null');
    if (!sent || sent.email !== normalise(email)) return false;
    return Boolean(sent.consent) || !consent;
  } catch (e) {
    // no storage, or something unreadable in it: send
    return false;
  }
}

/**
 * Remember a sign-up the Worker took, for alreadySent.
 * @param {string} email
 * @param {boolean} consent
 * @param {Storage|null} storage
 */
function rememberSent(email, consent, storage) {
  try {
    storage.setItem(SENT_KEY, JSON.stringify({email: normalise(email), consent: Boolean(consent)}));
  } catch (e) {
    // the game must play without storage; the Worker still dedupes
  }
}

/**
 * The email as the Worker and start_run compare it.
 * @param {string} email
 * @returns {string}
 */
function normalise(email) {
  return String(email || '').trim().toLowerCase();
}

/**
 * sendSignup
 * @param {{email: string, name: string, consent: boolean, token: string|null}} signup
 * @param {Object} [options]
 * @param {Function} [options.fetch] fetch implementation, for tests
 * @param {string} [options.origin] the page's origin; outside a page (node)
 *   there is no Worker to post to, so nothing is sent
 * @param {Storage|null} [options.storage] sessionStorage-like, for tests
 */
export function sendSignup({email, name, consent, token}, options = {}) {
  try {
    const origin = options.origin || (globalThis.location && globalThis.location.origin);
    const doFetch = options.fetch || globalThis.fetch;
    if (!origin || origin === 'null' || typeof doFetch !== 'function') {
      return;
    }
    const storage = 'storage' in options ? options.storage : sessionStore();
    if (alreadySent(email, consent, storage)) {
      return;
    }
    const sent = doFetch.call(globalThis, `${origin}${SIGNUP_PATH}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({email, name, consent: Boolean(consent), token}),
      // finishes even if the page moves on straight after START
      keepalive: true,
      credentials: 'same-origin'
    });
    if (sent && typeof sent.then === 'function') {
      // Remembered only once the Worker answers 2xx (sent, or nothing to
      // send), so a sign-up lost to a network or database failure is tried
      // again on the next game. A start that ran offline has no token, and
      // production answers it 204 without sending, so it is not remembered:
      // the next game's real run must still send it.
      sent.then((res) => {
        if (res && res.ok && token) {
          rememberSent(email, consent, storage);
        }
      }).catch(() => {});
    }
  } catch (e) {
    // a sign-up that can't be sent is not the player's problem
  }
}
